import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SensorsIcon from '@mui/icons-material/Sensors';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ControllerBoxIcon } from 'assets/images/icons/controller-box.svg';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import { brandColors } from 'types/colors';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../../LayoutContainers/DashboardLayout';
import { fetchAllDevicesLastRecordTimes } from '../../../stores/adminCenter/adminCenterSlice';

const INACTIVE_DEVICE_THRESHOLD_SECONDS = 10 * 60;
const LAST_ACTIVE_FILTERS = [
  { key: 'ALL', label: 'All', seconds: null },
  { key: 'LAST_DAY', label: 'Last day', seconds: 86400 },
  { key: 'LAST_2_DAYS', label: 'Last 2 days', seconds: 2 * 86400 },
  { key: 'LAST_3_DAYS', label: 'Last 3 days', seconds: 3 * 86400 },
  { key: 'LAST_4_DAYS', label: 'Last 4 days', seconds: 4 * 86400 },
  { key: 'LAST_5_DAYS', label: 'Last 5 days', seconds: 5 * 86400 },
  { key: 'LAST_6_DAYS', label: 'Last 6 days', seconds: 6 * 86400 },
  { key: 'LAST_WEEK', label: 'Last week', seconds: 7 * 86400 },
  { key: 'LAST_2_WEEKS', label: 'Last 2 weeks', seconds: 14 * 86400 },
];

const formatUnixTime = unixTime => {
  if (!unixTime) {
    return null;
  }

  return new Date(Number(unixTime) * 1000).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatInactivityDuration = unixTime => {
  if (!unixTime) {
    return 'No records';
  }

  const inactiveSeconds = Math.max(0, Math.floor(Date.now() / 1000) - Number(unixTime));
  const days = Math.floor(inactiveSeconds / 86400);
  const hours = Math.floor((inactiveSeconds % 86400) / 3600);
  const minutes = Math.floor((inactiveSeconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const getSensorLocation = sensor =>
  [sensor.building, sensor.floor_number ? `Floor ${sensor.floor_number}` : null, sensor.area_name].filter(Boolean).join(' / ');

export const InactiveSensorsReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    field: 'lastActiveTime',
    direction: 'desc',
  });
  const [selectedLastActiveFilter, setSelectedLastActiveFilter] = useState('ALL');
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const [selectedActionsDevice, setSelectedActionsDevice] = useState(null);
  const { devicesLastRecordTimes, devicesLastRecordTimesLoading, devicesLastRecordTimesError } = useSelector(
    state => state.adminCenter,
  );
  const selectedFilterObject = useSelector(state => state.persistence?.selectedFilterObject || {});

  useEffect(() => {
    dispatch(fetchAllDevicesLastRecordTimes());
  }, [dispatch]);

  const inactiveSensors = useMemo(() => {
    const { building = 'all', floor = 'all', area = 'all', hvacEquipment = 'all', searchString = '' } = selectedFilterObject;
    const normalizedSearchString = String(searchString || '')
      .trim()
      .toLowerCase();

    const selectedLastActiveFilterConfig = LAST_ACTIVE_FILTERS.find(filter => filter.key === selectedLastActiveFilter);
    const nowUnix = Math.floor(Date.now() / 1000);

    return devicesLastRecordTimes
      .filter(device => {
        const lastActiveTime = Number(device.sensor_last_reading_time || 0);
        const inactiveByTime =
          !lastActiveTime || Math.floor(Date.now() / 1000) - lastActiveTime > INACTIVE_DEVICE_THRESHOLD_SECONDS;

        return Number(device.status) === 4 || inactiveByTime;
      })
      .filter(device => {
        const matchesBuilding = building === 'all' || !building || String(device.building_id || '') === String(building);
        const matchesFloor = floor === 'all' || !floor || String(device.floor_number || '') === String(floor);
        const matchesArea = area === 'all' || !area || String(device.area_id || '') === String(area);
        const matchesHvacEquipment = hvacEquipment === 'all' || !hvacEquipment || (Array.isArray(device.hvac_equipment_ids) && device.hvac_equipment_ids.some(id => String(id) === String(hvacEquipment)));
        const searchableValues = [device.name, device.id, device.building, device.floor_number, device.area_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const matchesSearch = !normalizedSearchString || searchableValues.includes(normalizedSearchString);

        return matchesBuilding && matchesFloor && matchesArea && matchesHvacEquipment && matchesSearch;
      })
      .filter(device => {
        if (!selectedLastActiveFilterConfig?.seconds) {
          return true;
        }

        const lastActiveTime = Number(device.sensor_last_reading_time || 0);
        return lastActiveTime && lastActiveTime >= nowUnix - selectedLastActiveFilterConfig.seconds;
      })
      .map(device => {
        const lastActiveUnix = Number(device.sensor_last_reading_time || 0);

        return {
          ...device,
          lastActiveUnix,
          inactivitySeconds: lastActiveUnix
            ? Math.max(0, Math.floor(Date.now() / 1000) - lastActiveUnix)
            : Number.MAX_SAFE_INTEGER,
          location: getSensorLocation(device),
          lastActiveTime: formatUnixTime(device.sensor_last_reading_time),
          inactivityDuration: formatInactivityDuration(device.sensor_last_reading_time),
        };
      });
  }, [devicesLastRecordTimes, selectedFilterObject, selectedLastActiveFilter]);

  const sortedInactiveSensors = useMemo(() => {
    const sortMultiplier = sortConfig.direction === 'asc' ? 1 : -1;

    return [...inactiveSensors].sort((firstSensor, secondSensor) => {
      const firstValue =
        sortConfig.field === 'inactivityDuration' ? firstSensor.inactivitySeconds : firstSensor.lastActiveUnix || 0;
      const secondValue =
        sortConfig.field === 'inactivityDuration' ? secondSensor.inactivitySeconds : secondSensor.lastActiveUnix || 0;

      return (firstValue - secondValue) * sortMultiplier;
    });
  }, [inactiveSensors, sortConfig]);

  const handleSortClick = field => {
    setSortConfig(currentSort => ({
      field,
      direction: currentSort.field === field && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortHeader = (label, field) => {
    const isActiveSort = sortConfig.field === field;
    const isAscending = isActiveSort && sortConfig.direction === 'asc';

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>{label}</span>
        <IconButton size="small" aria-label={`Sort ${label}`} onClick={() => handleSortClick(field)} sx={{ p: 0.25 }}>
          {isAscending ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </Box>
    );
  };

  const handleLastActiveFilterClick = filterKey => {
    setSelectedLastActiveFilter(filterKey);
  };

  const handleRefreshClick = () => {
    dispatch(fetchAllDevicesLastRecordTimes());
  };

  const handleActionsMenuOpen = (event, device) => {
    setActionsMenuAnchorEl(event.currentTarget);
    setSelectedActionsDevice(device);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchorEl(null);
    setSelectedActionsDevice(null);
  };

  const handleActionsNavigate = path => {
    handleActionsMenuClose();
    navigate(path);
  };

  const selectedActionsDeviceName = selectedActionsDevice?.name || selectedActionsDevice?.id || 'device';
  const isSelectedActionsDeviceController = String(selectedActionsDevice?.device_type || '').startsWith('Controller');

  const emptyStateText = devicesLastRecordTimesError || 'No inactive sensors to display';

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Inactive Sensors Report" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <Box sx={{ mt: 3, mb: 2, px: { xs: 0, sm: 1.5 }, textAlign: { xs: 'center', sm: 'left' } }}>
        <Typography sx={{ color: '#152536', fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}>
          Sensor Monitoring
        </Typography>
        <Typography sx={{ color: '#6d7178', fontSize: 15, fontWeight: 500, mt: 0.75 }}>
          Track inactive sensors, recent device activity, and connectivity gaps across customers, buildings, floors, and areas.
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        <DevicesListFilter status={false} hvacEquipment />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 1, mt: 1, mb: 2 }}>
        {LAST_ACTIVE_FILTERS.map(filter => {
          const isSelected = selectedLastActiveFilter === filter.key;

          return (
            <Button
              key={filter.key}
              variant="inherit"
              disableElevation
              disableRipple
              disableFocusRipple
              onClick={() => handleLastActiveFilterClick(filter.key)}
              aria-pressed={isSelected}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                backgroundColor: isSelected ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                color: isSelected ? '#fff' : brandColors.BLUE,
                minHeight: 32,
                px: 2,
                '&:hover': {
                  backgroundColor: isSelected ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                  opacity: 0.9,
                },
                '&:active': {
                  backgroundColor: isSelected ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                },
                '&.Mui-focusVisible': {
                  boxShadow: 'none',
                },
              }}>
              {filter.label}
            </Button>
          );
        })}
        <IconButton
          aria-label="Refresh inactive devices"
          onClick={handleRefreshClick}
          disabled={devicesLastRecordTimesLoading}
          sx={{
            width: 32,
            height: 32,
            color: brandColors.BLUE,
            backgroundColor: brandColors.BLUE_LIGHT,
            '&:hover': {
              backgroundColor: brandColors.BLUE_LIGHT,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              color: brandColors.GREY,
              backgroundColor: brandColors.GREY_LIGHT,
            },
          }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ mt: 1 }}>
        <TableContainer
          component={Paper}
          sx={{
            height: {
              xs: 'calc(100dvh - 405px)',
              sm: 'calc(100dvh - 380px)',
            },
            minHeight: 260,
          }}>
          <Table
            stickyHeader
            size="small"
            aria-label="Inactive sensors report"
            sx={{
              minWidth: 950,
              tableLayout: 'fixed',
            }}>
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell align="center">#</TableCell>
                <TableCell>Sensor name</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>{renderSortHeader('Last active time', 'lastActiveTime')}</TableCell>
                <TableCell>{renderSortHeader('Inactivity Duration', 'inactivityDuration')}</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devicesLastRecordTimesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text">
                      Loading inactive sensors...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : inactiveSensors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color={devicesLastRecordTimesError ? 'error' : 'text'}>
                      {emptyStateText}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedInactiveSensors.map((sensor, index) => (
                  <TableRow key={sensor.id || index} hover>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell>{sensor.name || '—'}</TableCell>
                    <TableCell>{sensor.id || '—'}</TableCell>
                    <TableCell>{sensor.location || '—'}</TableCell>
                    <TableCell>{sensor.lastActiveTime || '—'}</TableCell>
                    <TableCell>{sensor.inactivityDuration || '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label={`Open actions for ${sensor.name || sensor.id || 'device'}`}
                        onClick={event => handleActionsMenuOpen(event, sensor)}
                        sx={{
                          color: brandColors.BLUE,
                          '&:hover': {
                            backgroundColor: brandColors.BLUE_LIGHT,
                          },
                        }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Menu
        anchorEl={actionsMenuAnchorEl}
        open={Boolean(actionsMenuAnchorEl)}
        onClose={handleActionsMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          sx: {
            p: 0.5,
          },
        }}
        PaperProps={{
          sx: {
            mt: 0.75,
            minWidth: 310,
            borderRadius: '8px',
            border: `1px solid ${brandColors.BLUE_LIGHT}`,
            backgroundColor: '#fff',
            boxShadow: '0 16px 32px -20px rgba(50, 68, 226, 0.5), 0 12px 24px -18px rgba(0, 0, 0, 0.35)',
          },
        }}>
        {selectedActionsDevice && (
          <MenuItem
            onClick={() => handleActionsNavigate(`/data-explorer/${selectedActionsDevice.id}`)}
            sx={{
              borderRadius: '6px',
              color: '#1f2937',
              fontSize: '0.9rem',
              fontWeight: 600,
              minHeight: 42,
              px: 1.25,
              '&:hover': {
                backgroundColor: brandColors.BLUE_LIGHT,
                color: brandColors.BLUE,
              },
            }}>
            <ListItemIcon sx={{ color: brandColors.BLUE, minWidth: '30px !important' }}>
              <SensorsIcon fontSize="small" />
            </ListItemIcon>
            Go to {selectedActionsDeviceName} sensor page
          </MenuItem>
        )}
        {selectedActionsDevice && isSelectedActionsDeviceController && (
          <MenuItem
            onClick={() => handleActionsNavigate(`/controller/${selectedActionsDevice.id}`)}
            sx={{
              borderRadius: '6px',
              color: '#1f2937',
              fontSize: '0.9rem',
              fontWeight: 600,
              minHeight: 42,
              px: 1.25,
              '&:hover': {
                backgroundColor: brandColors.BLUE_LIGHT,
                color: brandColors.BLUE,
              },
            }}>
            <ListItemIcon sx={{ color: brandColors.BLUE, minWidth: '30px !important' }}>
              <ControllerBoxIcon width={18} height={18} fill={brandColors.BLUE} />
            </ListItemIcon>
            Go to {selectedActionsDeviceName} controller page
          </MenuItem>
        )}
      </Menu>
    </DashboardLayout>
  );
};

export default InactiveSensorsReport;
